import React from 'react';
import { Button } from 'react-bootstrap';
import { Bell, Check2, Trash, ChevronRight } from 'react-bootstrap-icons';
import './NotificationDropdown.css';

export interface NotificationItem {
  id: string;
  message: string;
  isRead: boolean;
  createdAt: string;
  tag?: string;
  direction?: string;
  recruiterEmail?: string;
}

interface NotificationDropdownProps {
  show: boolean;
  notifications: NotificationItem[];
  showAll: boolean;
  onToggleShowAll: () => void;
  onClose: () => void;
  onOpen: (n: NotificationItem) => void;
  onMarkRead: (n: NotificationItem) => void;
  onDelete: (n: NotificationItem) => void;
  canRespondToProfileAccess: (n: NotificationItem) => boolean;
  onRespondProfileAccess: (n: NotificationItem, action: 'accept' | 'deny') => void;
}

function formatRelativeTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  show,
  notifications,
  showAll,
  onToggleShowAll,
  onClose,
  onOpen,
  onMarkRead,
  onDelete,
  canRespondToProfileAccess,
  onRespondProfileAccess,
}) => {
  if (!show) return null;

  const unreadCount = notifications.filter((n) => !n.isRead).length;
  const visible = showAll ? notifications : notifications.slice(0, 7);
  const hiddenCount = Math.max(0, notifications.length - 7);

  return (
    <>
      <button
        type="button"
        className="notif-dropdown-backdrop"
        aria-label="Close notifications"
        onClick={onClose}
      />
      <div className="notif-dropdown-panel" role="dialog" aria-label="Notifications">
        <div className="notif-dropdown-header">
          <div className="notif-dropdown-header-left">
            <span className="notif-dropdown-icon-wrap" aria-hidden>
              <Bell size={18} />
            </span>
            <div>
              <h6 className="notif-dropdown-title">Notifications</h6>
              {unreadCount > 0 ? (
                <p className="notif-dropdown-sub">{unreadCount} unread</p>
              ) : (
                <p className="notif-dropdown-sub">You&apos;re all caught up</p>
              )}
            </div>
          </div>
          <button
            type="button"
            className="notif-dropdown-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="notif-dropdown-body">
          {notifications.length === 0 ? (
            <div className="notif-dropdown-empty">
              <Bell size={28} className="notif-dropdown-empty-icon" />
              <p>No notifications yet</p>
              <span>Updates about messages, meetings, and your profile will appear here.</span>
            </div>
          ) : (
            <ul className="notif-dropdown-list">
              {visible.map((n) => {
                const showAccessActions = canRespondToProfileAccess(n);
                return (
                  <li
                    key={n.id}
                    className={`notif-dropdown-item ${!n.isRead ? 'notif-dropdown-item--unread' : ''}`}
                  >
                    {!n.isRead && <span className="notif-dropdown-unread-dot" aria-hidden />}
                    <div className="notif-dropdown-item-row">
                      <button
                        type="button"
                        className="notif-dropdown-item-main"
                        onClick={() => onOpen(n)}
                      >
                        <p className="notif-dropdown-message">{n.message}</p>
                        <time className="notif-dropdown-time">
                          {formatRelativeTime(n.createdAt)}
                        </time>
                      </button>
                      <div className="notif-dropdown-item-actions">
                        {!n.isRead && (
                          <button
                            type="button"
                            className="notif-dropdown-action-btn"
                            title="Mark as read"
                            onClick={() => onMarkRead(n)}
                          >
                            <Check2 size={15} />
                          </button>
                        )}
                        <button
                          type="button"
                          className="notif-dropdown-action-btn notif-dropdown-action-btn--danger"
                          title="Delete"
                          onClick={() => onDelete(n)}
                        >
                          <Trash size={15} />
                        </button>
                      </div>
                    </div>
                    {showAccessActions && (
                      <div className="notif-dropdown-access-actions">
                        <Button
                          size="sm"
                          variant="success"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRespondProfileAccess(n, 'accept');
                          }}
                        >
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRespondProfileAccess(n, 'deny');
                          }}
                        >
                          Deny
                        </Button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {notifications.length > 7 && (
          <div className="notif-dropdown-footer">
            <button type="button" className="notif-dropdown-more" onClick={onToggleShowAll}>
              {showAll ? 'Show less' : `View ${hiddenCount} more`}
              <ChevronRight
                size={14}
                className={showAll ? 'notif-dropdown-more-chevron--up' : ''}
              />
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default NotificationDropdown;
